import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { CalendarDays, Clock, MapPin, Navigation, Share2, Facebook, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { publicApi, eventPhotoUrl } from "@/lib/api";
import { IMG } from "@/lib/content";
import { useContent } from "@/lib/content-store";
import { timeLabel } from "@/components/staff/categories";
import { ContactForm } from "@/components/ContactForm";
import { AddToCalendar } from "@/components/AddToCalendar";

const Rsvp = ({ ev }) => {
  const id = ev.id;
  const [count, setCount] = useState(null);
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  useEffect(() => { publicApi.get(`/public/events/${id}/rsvp-count`).then((r) => setCount(r.data.headcount)).catch(() => {}); }, [id]);
  return (
    <div className="space-y-3 border-t border-slate-100 pt-4" data-testid="event-rsvp">
      {count > 0 && <p className="text-sm text-slate-600" data-testid="event-rsvp-count"><b className="text-[#0A3A6B]">{count}</b> {count === 1 ? "person is" : "people are"} coming</p>}
      {open ? <ContactForm path={`/public/events/${id}/rsvp`} guests cta="Count me in" testPrefix="rsvp" done="See you there! We've saved your spot. If you shared your email, we'll send a reminder the day before." onDone={(d) => { setCount(d.headcount); setDone(true); const ids = JSON.parse(localStorage.getItem("vp_rsvps") || "[]"); if (!ids.includes(id)) localStorage.setItem("vp_rsvps", JSON.stringify([...ids, id].slice(-50))); }} />
        : <Button onClick={() => setOpen(true)} className="w-full rounded-full bg-[#1D64B4] hover:bg-[#0A3A6B]" data-testid="event-rsvp-button">I'm coming</Button>}
      {done && <div className="space-y-2" data-testid="rsvp-calendar"><p className="text-xs font-semibold uppercase tracking-wider text-[#1D64B4]">Save it to your calendar</p><AddToCalendar ev={ev} /></div>}
    </div>
  );
};

const shareLink = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/share/events/${id}`;

const share = async (title, url) => {
  if (navigator.share) {
    try { await navigator.share({ title, url }); } catch (e) { /* cancelled */ }
    return;
  }
  await navigator.clipboard.writeText(url);
  toast.success("Link copied — paste it anywhere to share");
};

const Directions = ({ place }) => (
  <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white" data-testid="event-directions">
    <iframe title="Map" className="h-64 w-full sm:h-80" loading="lazy"
      src={`https://maps.google.com/maps?q=${encodeURIComponent(place)}&output=embed`} />
    <div className="flex flex-wrap items-center justify-between gap-3 p-5">
      <p className="flex items-center gap-2 text-sm text-slate-600"><MapPin className="h-4 w-4 text-[#1D64B4]" />{place}</p>
      <Button asChild className="rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="event-get-directions">
        <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(place)}`} target="_blank" rel="noreferrer">
          <Navigation className="mr-1 h-4 w-4" /> Get directions
        </a>
      </Button>
    </div>
  </div>
);

export default function EventPage() {
  const { id } = useParams();
  const church = useContent();
  const [ev, setEv] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    publicApi.get(`/public/events/${id}`).then((r) => setEv(r.data)).catch(() => setMissing(true));
  }, [id]);

  useEffect(() => {
    if (ev) document.title = `${ev.title} · Victory Parañaque`;
    return () => { document.title = "Victory Parañaque"; };
  }, [ev]);

  const place = ev?.location || church.address;
  const time = ev?.start_time ? `${timeLabel(ev.start_time)}${ev.end_time ? ` – ${timeLabel(ev.end_time)}` : ""}` : "";

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="event-page">
      <SiteHeader />
      {missing && (
        <main className="mx-auto max-w-3xl px-5 pb-24 pt-36 text-center">
          <h1 className="text-3xl font-bold text-slate-900" data-testid="event-not-found">We couldn't find this event</h1>
          <p className="mt-3 text-slate-500">It may have already passed. We'd love to see you at what's coming up next.</p>
          <Link to="/events" className="mt-6 inline-block font-semibold text-[#1D64B4]">See upcoming events</Link>
        </main>
      )}
      {ev && (
        <>
          <section className="relative h-[46vh] min-h-[320px] overflow-hidden bg-[#0A3A6B] pt-[72px]">
            <img src={ev.has_photo ? eventPhotoUrl(ev.id) : IMG.praise} alt={ev.title} className="absolute inset-0 h-full w-full object-cover" data-testid="event-photo" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A3A6B] via-[#0A3A6B]/50 to-transparent" />
            <div className="relative mx-auto flex h-full max-w-5xl flex-col justify-end px-5 pb-10 lg:px-10">
              <Link to="/events" className="mb-4 flex w-fit items-center gap-1 text-sm text-sky-100 hover:text-white" data-testid="event-back-link"><ArrowLeft className="h-4 w-4" /> All events</Link>
              <h1 className="text-3xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="event-title">{ev.title}</h1>
            </div>
          </section>
          <main className="mx-auto grid max-w-5xl gap-8 px-5 py-10 lg:grid-cols-[1.5fr_1fr] lg:px-10 lg:py-14">
            <div className="space-y-8">
              {ev.description && <p className="whitespace-pre-line text-base leading-relaxed text-slate-700" data-testid="event-description">{ev.description}</p>}
              {place && <Directions place={place} />}
            </div>
            <aside className="order-first h-fit space-y-4 rounded-3xl border border-slate-200 bg-white p-6 lg:order-none lg:sticky lg:top-24">
              <p className="flex items-center gap-3 text-slate-800" data-testid="event-date"><CalendarDays className="h-5 w-5 text-[#1D64B4]" />{format(parseISO(ev.date), "EEEE, MMMM d, yyyy")}</p>
              {time && <p className="flex items-center gap-3 text-slate-800" data-testid="event-time"><Clock className="h-5 w-5 text-[#1D64B4]" />{time}</p>}
              {ev.location && <p className="flex items-center gap-3 text-slate-800"><MapPin className="h-5 w-5 text-[#1D64B4]" />{ev.location}</p>}
              {ev.date >= format(new Date(), "yyyy-MM-dd") && <Rsvp ev={ev} />}
              <div className="flex flex-col gap-2 border-t border-slate-100 pt-4">
                <Button onClick={() => share(ev.title, shareLink(ev.id))} className="rounded-full bg-[#0A3A6B] hover:bg-[#1D64B4]" data-testid="event-share-button"><Share2 className="mr-1 h-4 w-4" /> Share this event</Button>
                <Button asChild variant="outline" className="rounded-full" data-testid="event-facebook-share">
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink(ev.id))}`} target="_blank" rel="noreferrer"><Facebook className="mr-1 h-4 w-4" /> Share on Facebook</a>
                </Button>
              </div>
            </aside>
          </main>
        </>
      )}
      <SiteFooter />
    </div>
  );
}
